/**
 * Тренажер мінімальних пар.
 *
 * Логіка вправи: спершу **розрізнити на слух**, і лише потім вимовляти.
 * Поки вухо зводить ł і l в один звук, тренувати вимову марно — людина
 * не чує власної помилки. Тому екран іде у два кроки: слухання, потім вимова.
 */
import { useEffect, useMemo, useState } from 'react'
import { useStore } from '../lib/store.ts'
import { kv } from '../lib/db.ts'
import { plural, shuffle } from '../lib/text.ts'
import { preload, unlockAudio } from '../lib/audio.ts'
import { Bar, Prose, RecordCompare, Speak, TopBar } from '../components/ui.tsx'
import type { Contrast, MinimalPair } from '../../content/schema.ts'

/** Скільки разів за підхід треба зробити вибір. */
const ROUNDS = 12

export interface ContrastScore { rounds: number; correct: number }
type Scores = Record<string, ContrastScore>

async function saveScore(id: string, rounds: number, correct: number) {
  const all = (await kv.get<Scores>('contrastScores').catch(() => undefined)) ?? {}
  const prev = all[id] ?? { rounds: 0, correct: 0 }
  all[id] = { rounds: prev.rounds + rounds, correct: prev.correct + correct }
  await kv.set('contrastScores', all).catch(() => undefined)
}

// ── Список опозицій ───────────────────────────────────────────────────────

export function PairsList() {
  const { idx } = useStore()
  const [scores, setScores] = useState<Scores>({})

  useEffect(() => {
    kv.get<Scores>('contrastScores').then((s) => setScores(s ?? {})).catch(() => undefined)
  }, [])

  const contrasts = idx.course.contrasts ?? []

  return (
    <>
      <TopBar title="Розрізнення звуків" />
      <div className="screen stack">
        <div className="card stack--tight stack">
          <h2>Спочатку почути, потім сказати</h2>
          <p className="small muted">
            Українське вухо зводить ł і l, sz і ś в один звук — не через неуважність,
            а тому що в рідній мові вони не розрізняють слів. Поки різниця не чутна,
            вимовляти її навмання марно.
          </p>
          <p className="small muted">
            Тут пари справжніх слів, що відрізняються рівно одним звуком.
            Спершу вгадуєш на слух, далі вимовляєш і порівнюєш із зразком.
          </p>
        </div>

        <div className="list">
          {contrasts.map((ctr) => {
            const s = scores[ctr.id]
            const pct = s && s.rounds > 0 ? Math.round((s.correct / s.rounds) * 100) : null
            return (
              <a className="item" key={ctr.id} href={`#/pairs/${ctr.id}`}>
                <span className="item__emoji" aria-hidden>{ctr.emoji}</span>
                <span className="item__body">
                  <span className="item__title">{ctr.title}</span>
                  <span className="item__sub">
                    {ctr.pairs.length} {plural(ctr.pairs.length, 'пара', 'пари', 'пар')}
                    {pct !== null && ` · точність ${pct}%`}
                  </span>
                  {pct !== null && (
                    <span style={{ display: 'block', marginTop: 6 }}>
                      <Bar value={s!.correct} max={s!.rounds} good={pct >= 85} />
                    </span>
                  )}
                </span>
                <span className="item__after">→</span>
              </a>
            )
          })}
        </div>
      </div>
    </>
  )
}

// ── Тренажер однієї опозиції ──────────────────────────────────────────────

interface Round { pair: MinimalPair; side: 'a' | 'b' }

export function PairsDrill({ id }: { id: string }) {
  const { idx } = useStore()
  const contrast = (idx.course.contrasts ?? []).find((c) => c.id === id)

  const [seed, setSeed] = useState(() => Math.floor(Date.now() / 1000))
  const [phase, setPhase] = useState<'intro' | 'listen' | 'result' | 'speak'>('intro')
  const [pos, setPos] = useState(0)
  const [picked, setPicked] = useState<'a' | 'b' | null>(null)
  const [correct, setCorrect] = useState(0)
  const [misses, setMisses] = useState<string[]>([])
  const [speakIdx, setSpeakIdx] = useState(0)

  const rounds = useMemo<Round[]>(() => {
    if (!contrast) return []
    const out: Round[] = []
    // Циклимо пари, щоб кожна прозвучала однакову кількість разів,
    // і перемішуємо, щоб порядок не запамʼятовувався.
    for (let i = 0; i < ROUNDS; i++) {
      const pair = contrast.pairs[i % contrast.pairs.length]!
      out.push({ pair, side: (seed + i * 7 + pair.id.length) % 2 === 0 ? 'a' : 'b' })
    }
    return shuffle(out, seed)
  }, [contrast, seed])

  useEffect(() => { unlockAudio() }, [])
  useEffect(() => {
    preload(rounds.slice(pos, pos + 4).flatMap((r) => [r.pair.a.audio, r.pair.b.audio]))
  }, [rounds, pos])

  if (!contrast) return <div className="screen">Опозицію не знайдено</div>

  const words = contrast.pairs.flatMap((p) => [p.a, p.b])

  const restart = () => {
    setSeed(Math.floor(Date.now() / 1000) + 1)
    setPhase('listen'); setPos(0); setPicked(null); setCorrect(0); setMisses([])
  }

  // — крок 0: що саме слухати —
  if (phase === 'intro') {
    return (
      <>
        <TopBar title={contrast.title} />
        <div className="screen stack">
          <ContrastIntro contrast={contrast} />

          <div className="card stack--tight stack">
            <strong>Пари цієї опозиції</strong>
            {contrast.pairs.map((p) => (
              <div className="row row--between" key={p.id}>
                <span className="small">
                  <b>{p.a.pl}</b> <span className="faint">{p.a.uk}</span>
                  {' ~ '}
                  <b>{p.b.pl}</b> <span className="faint">{p.b.uk}</span>
                </span>
                <span className="row" style={{ gap: 4 }}>
                  <Speak audio={p.a.audio} text={p.a.pl} small />
                  <Speak audio={p.b.audio} text={p.b.pl} small />
                </span>
              </div>
            ))}
            <p className="tiny faint">
              Послухай кожну пару підряд кілька разів, перш ніж починати —
              так вправа буде не вгадуванням, а розрізненням.
            </p>
          </div>

          <button className="btn btn--primary btn--wide" onClick={() => setPhase('listen')}>
            Почати ({ROUNDS} питань)
          </button>
        </div>
      </>
    )
  }

  // — крок 1: слухання —
  if (phase === 'listen') {
    const round = rounds[pos]
    if (!round) return null
    const answer = round.side
    const said = round.pair[answer]

    const choose = (side: 'a' | 'b') => {
      setPicked(side)
      if (side === answer) setCorrect((c) => c + 1)
      else setMisses((m) => (m.includes(round.pair.id) ? m : [...m, round.pair.id]))
    }

    const next = () => {
      if (pos + 1 >= rounds.length) {
        void saveScore(contrast.id, rounds.length, correct)
        setPhase('result')
      } else {
        setPos((p) => p + 1)
        setPicked(null)
      }
    }

    return (
      <div className="study">
        <div className="topbar">
          <a className="back" href="#/pairs" aria-label="Назад">←</a>
          <div style={{ flex: 1 }}><Bar value={pos} max={rounds.length} /></div>
          <span className="tiny faint" style={{ minWidth: 46, textAlign: 'right' }}>
            {pos + 1} / {rounds.length}
          </span>
        </div>

        <div className="study__body">
          <span className="chip">яке слово прозвучало?</span>
          <Speak audio={said.audio} text={said.pl} auto key={`${pos}-${answer}`} />
          <p className="tiny faint">Натисни, щоб повторити. Утримуй — повільно.</p>

          <div className="stack" style={{ width: '100%', marginTop: 8 }}>
            {(['a', 'b'] as const).map((side) => {
              const w = round.pair[side]
              const cls = picked
                ? side === answer ? ' is-correct' : side === picked ? ' is-wrong' : ' is-muted'
                : ''
              return (
                <button key={side} className={`option${cls}`} disabled={!!picked} onClick={() => choose(side)}>
                  <div style={{ fontWeight: 600, fontSize: 19 }}>{w.pl}</div>
                  <div className="option__uk">{w.uk}</div>
                  {picked && w.tr && <div className="tiny" style={{ color: 'var(--accent)', marginTop: 3 }}>{w.tr}</div>}
                </button>
              )
            })}
          </div>

          {picked && (
            <div className={`feedback feedback--${picked === answer ? 'ok' : 'no'}`}>
              {picked === answer ? 'Так, це воно' : `Прозвучало «${said.pl}» — ${said.uk}`}
            </div>
          )}
          {picked && picked !== answer && (
            <div className="row" style={{ gap: 10 }}>
              <Speak audio={round.pair.a.audio} text={round.pair.a.pl} small />
              <span className="tiny faint">послухай обидва підряд</span>
              <Speak audio={round.pair.b.audio} text={round.pair.b.pl} small />
            </div>
          )}
        </div>

        <div style={{ padding: 'var(--sp-3) var(--sp-4) var(--sp-4)' }}>
          <button className="btn btn--primary btn--wide" disabled={!picked} onClick={next}>Далі</button>
        </div>
      </div>
    )
  }

  // — підсумок слухання —
  if (phase === 'result') {
    const pct = Math.round((correct / rounds.length) * 100)
    const weak = contrast.pairs.filter((p) => misses.includes(p.id))
    return (
      <>
        <TopBar title={contrast.title} />
        <div className="screen stack">
          <div className="card center stack">
            <div style={{ fontSize: 48 }}>{pct >= 85 ? '👂' : pct >= 60 ? '🙂' : '🌱'}</div>
            <h2>{correct} з {rounds.length}</h2>
            <p className="muted small">
              {pct >= 85
                ? 'Вухо різницю чує. Тепер має сенс тренувати вимову.'
                : pct >= 60
                ? 'Різниця вже вловлюється, але нестійко. Ще один підхід — і буде помітно легше.'
                : 'Поки що вухо зводить ці звуки в один. Це нормально на початку: пройди ще пару разів, слухаючи обидва слова підряд.'}
            </p>
          </div>

          {weak.length > 0 && (
            <div className="card stack--tight stack">
              <strong>Складні пари</strong>
              {weak.map((p) => (
                <div className="row row--between" key={p.id}>
                  <span className="small">
                    <b>{p.a.pl}</b> <span className="faint">{p.a.uk}</span>
                    {' ~ '}
                    <b>{p.b.pl}</b> <span className="faint">{p.b.uk}</span>
                  </span>
                  <span className="row" style={{ gap: 4 }}>
                    <Speak audio={p.a.audio} text={p.a.pl} small />
                    <Speak audio={p.b.audio} text={p.b.pl} small />
                  </span>
                </div>
              ))}
            </div>
          )}

          <button className="btn btn--primary btn--wide" onClick={() => { setPhase('speak'); setSpeakIdx(0) }}>
            Тепер вимов сам
          </button>
          <button className="btn btn--wide" onClick={restart}>Ще один підхід на слух</button>
          <a className="btn btn--ghost btn--wide" href="#/pairs">До інших опозицій</a>
        </div>
      </>
    )
  }

  // — крок 2: вимова —
  const word = words[speakIdx]
  if (!word) {
    return (
      <>
        <TopBar title={contrast.title} />
        <div className="screen stack">
          <div className="card center stack">
            <div style={{ fontSize: 48 }}>🎯</div>
            <h2>Опозицію пройдено</h2>
            <p className="muted small">
              Повертайся до неї час від часу: слух на нові звуки закріплюється
              повтореннями, а не одним підходом.
            </p>
            <a className="btn btn--primary btn--wide" href="#/pairs">До інших опозицій</a>
            <button className="btn btn--ghost btn--wide" onClick={restart}>Пройти цю ще раз</button>
          </div>
        </div>
      </>
    )
  }

  const partner = words[speakIdx % 2 === 0 ? speakIdx + 1 : speakIdx - 1]

  return (
    <>
      <TopBar title="Вимов сам" />
      <div className="screen stack">
        <Bar value={speakIdx} max={words.length} />
        <div className="card center stack">
          <span className="chip">{speakIdx + 1} з {words.length}</span>
          <div className="study__pl">{word.pl}</div>
          <div className="study__uk">{word.uk}</div>
          {word.tr && <div className="study__tr">{word.tr}</div>}
          <Speak audio={word.audio} text={word.pl} />
          <RecordCompare phraseId={`pair:${word.pl}`} audio={word.audio} text={word.pl} />
          {partner && (
            <div className="row" style={{ gap: 8, justifyContent: 'center' }}>
              <span className="tiny faint">порівняй із парою:</span>
              <span className="small"><b>{partner.pl}</b></span>
              <Speak audio={partner.audio} text={partner.pl} small />
            </div>
          )}
        </div>
        <button className="btn btn--primary btn--wide" onClick={() => setSpeakIdx((i) => i + 1)}>
          {speakIdx + 1 >= words.length ? 'Завершити' : 'Наступне слово'}
        </button>
      </div>
    </>
  )
}

/** Пояснення до опозиції — показуємо перед першим підходом. */
export function ContrastIntro({ contrast }: { contrast: Contrast }) {
  return (
    <div className="card stack--tight stack">
      <strong>{contrast.title}</strong>
      <Prose text={contrast.hint} />
      <p className="small muted">{contrast.why}</p>
    </div>
  )
}
